import { i18n } from '@locales';

// Header labels (besides the localized "phone number") that identify the phone column, so
// CSV/Excel uploads work regardless of the exporter's wording or the UI language. Matched
// case-insensitively as a substring, so "Phone", "Phone (Excel)", "Phone number" and
// "מספר טלפון" all resolve to the phone column.
const PHONE_HEADER_KEYWORDS = ['phone', 'טלפון', 'פלאפון', 'נייד'];

/**
 * Parse a single CSV row, respecting quoted fields — commas and escaped quotes ("")
 * inside "..." are kept intact, so columns don't shift when another field contains a
 * comma (e.g. "Smith, Jr"). Plain split(',') would mis-align the phone column.
 * @param {string} row
 * @returns {string[]}
 */
export function parseCsvRow(row = '') {
    const out = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < row.length; i += 1) {
        const ch = row[i];
        if (ch === '"') {
            if (inQuotes && row[i + 1] === '"') {
                cur += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
        } else if (ch === ',' && !inQuotes) {
            out.push(cur);
            cur = '';
        } else {
            cur += ch;
        }
    }
    out.push(cur);
    return out;
}

/**
 * Find the index of the phone-number column in a header row.
 * @param {string[]} headerCells - the split header row
 * @returns {number} column index, or -1 when no phone column is found
 */
export function findPhoneColumnIndex(headerCells) {
    const keywords = [i18n.t('sms.phone_number'), ...PHONE_HEADER_KEYWORDS]
        .map(s => (s || '').trim().toLowerCase())
        .filter(Boolean);
    return headerCells.findIndex(cell => {
        const c = (cell || '').trim().toLowerCase();
        return c !== '' && keywords.some(k => c.includes(k));
    });
}

export function extractSpecificCsvFormatPhoneNumbers(csvData) {
    const rows = csvData.split('\n'); // Split by newline
    const arrayResult = [];

    // Find the phone column flexibly (any common header label / locale).
    const headerRow = parseCsvRow(rows[0]);
    const phoneIndex = findPhoneColumnIndex(headerRow);

    if (phoneIndex === -1) {
        // No recognizable phone column. Do NOT scan every cell — that would pull phone-like
        // values out of unrelated columns (order numbers / IDs) and text the wrong people.
        // Returning [] lets the caller surface a "no phone numbers found" message instead.
        return arrayResult;
    }

    // Phone column found — take only that column.
    rows.slice(1).forEach(row => {
        const values = parseCsvRow(row);
        if (values[phoneIndex]) {
            arrayResult.push(values[phoneIndex]);
        }
    });

    return arrayResult;
}
